import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CartAbandonmentRate = () => {
  // State variables for Cart Abandonment Rate calculation
  const [abandonedCarts, setAbandonedCarts] = useState(0); // Number of abandoned carts
  const [initiatedCheckouts, setInitiatedCheckouts] = useState(0); // Number of initiated checkouts
  const [abandonmentRate, setAbandonmentRate] = useState(0); // Calculated cart abandonment rate

  // Event handlers for input changes
  const handleAbandonedCartsChange = (e) =>
    setAbandonedCarts(parseFloat(e.target.value) || 0);
  const handleInitiatedCheckoutsChange = (e) =>
    setInitiatedCheckouts(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Cart Abandonment Rate
  useEffect(() => {
    if (initiatedCheckouts > 0) {
      const rate = (abandonedCarts / initiatedCheckouts) * 100;
      setAbandonmentRate(rate);
    }
    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
    
  }, [abandonedCarts, initiatedCheckouts]); // Recalculates when inputs change

  return (
    <Layout title="Cart Abandonment Rate Calculator" path="cart-abandonment-rate">
      <section className="section-padding" id="cart-abandonment-rate">
        <div className="container mt-5">
          <div className="row">
            {/* Cart Abandonment Rate Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Cart Abandonment Rate Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Abandoned Carts */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="abandonedCarts" className="form-label">
                        Abandoned Carts
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Abandoned Carts:</strong> The total number of shopping carts created by customers but not completed by a purchase."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="abandonedCarts"
                        placeholder="Enter number of abandoned carts"
                        value={abandonedCarts || ""}
                        onChange={handleAbandonedCartsChange}
                      />
                    </div>

                    {/* Input for Initiated Checkouts */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="initiatedCheckouts" className="form-label">
                        Initiated Checkouts
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Initiated Checkouts:</strong> The total number of shopping carts where the customer began the checkout process but did not complete the purchase."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="initiatedCheckouts"
                        placeholder="Enter number of initiated checkouts"
                        value={initiatedCheckouts || ""}
                        onChange={handleInitiatedCheckoutsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Cart Abandonment Rate:{" "}
                        <span className="fw-bold ms-3">
                          {abandonmentRate === 0
                            ? "0%"
                            : `${abandonmentRate.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}%`}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Cart Abandonment Rate Calculation
                </h3>

                {/* What is Cart Abandonment Rate? */}
                <h4 className="mt-3 mb-3">What is Cart Abandonment Rate?</h4>
                <p>
                  **Cart Abandonment Rate** is a key performance indicator (KPI) that measures the percentage of shopping carts created by customers that are not converted into purchases. It highlights the effectiveness of the checkout process.
                </p>

                {/* Why Use the Cart Abandonment Rate Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Cart Abandonment Rate Calculator?</h4>
                <p>
                  This calculator helps identify issues in the purchasing funnel, optimize the checkout process, and improve overall sales conversion rates.
                </p>

                {/* Benefits of Using the Cart Abandonment Rate Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Cart Abandonment Rate Calculator</h4>
                <ul>
                  <li>
                    <strong>Identifies Checkout Issues:</strong> Spot obstacles preventing customers from completing purchases.
                  </li>
                  <li>
                    <strong>Improves Conversion Rates:</strong> Reduces cart abandonment by addressing customer pain points.
                  </li>
                  <li>
                    <strong>Enhances Revenue:</strong> Maximizes the value of marketing and website efforts.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Cart Abandonment Rate Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Online Retailers:</strong> To monitor and improve checkout completion rates.
                  </li>
                  <li>
                    <strong>E-commerce Managers:</strong> To evaluate and enhance customer experience.
                  </li>
                  <li>
                    <strong>Digital Marketing Teams:</strong> To optimize campaigns aimed at reducing cart abandonment.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">What is a good cart abandonment rate?</h5>
                <p>
                  A good cart abandonment rate varies by industry, but rates below 40% are generally considered excellent. Average rates range between 60% to 80%.
                </p>

                <h5 className="mt-3 mb-2">How can I reduce cart abandonment?</h5>
                <p>
                  Simplify the checkout process, offer multiple payment options, use trust signals (e.g., secure checkout), and send follow-up emails or reminders to customers with abandoned carts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CartAbandonmentRate;
